import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { consoleConfig } from '../config';
import { BUTTON_PRIMARY_LG, CALLBACK_CARD, CALLBACK_SCREEN } from '../controlStyles';
import { sanitizeReturnPath } from '../routing';
import { openPersonalInNative } from './bridge';
import { getEmbedMarker, type EmbedMarker } from './embedMarker';
import { postRoute } from './nativeBridgeApi';
import {
  hydrateNativeSession,
  NativeSessionError,
  nativeSessionErrorMessage,
  requestNativeSession,
  type HydratableSessionClient,
  type NativeSessionTokens,
} from './nativeSession';

const ORG_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** A same-origin console path to land on, never back into an auth route. */
export function nativeReturnPath(value: string | null, origin = window.location.origin): string {
  if (!sanitizeReturnPath(value)) return '/';
  // Judge the path the browser will actually navigate to: dot segments (also
  // percent-encoded) can collapse `/x/..//host` into a protocol-relative URL.
  let url: URL;
  try {
    url = new URL(value as string, origin);
  } catch {
    return '/';
  }
  if (url.origin !== origin || url.pathname.startsWith('//') || url.pathname.startsWith('/authenticate')) return '/';
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The org id a console path names, when its first segment is a Stytch org id. */
export function orgIdFromConsolePath(pathname: string): string | null {
  const raw = /^\/org\/([^/]+)/.exec(pathname)?.[1];
  let segment: string | null = null;
  try {
    segment = raw ? decodeURIComponent(raw) : null;
  } catch {
    segment = null;
  }
  return segment && segment.startsWith('organization-') && ORG_ID_PATTERN.test(segment) ? segment : null;
}

type SignInState =
  | { status: 'working' }
  | { status: 'failed'; message: string };

export interface NativeSignInProps {
  orgId: string | null;
  reason: 'switch-org' | 'expired';
  returnTo: string;
  marker?: EmbedMarker | null;
  client?: HydratableSessionClient;
  request?: (orgId: string | null, reason: 'switch-org' | 'expired') => Promise<NativeSessionTokens>;
  /** Full navigation by default: a reload makes every SDK consumer read the adopted cookies. */
  onSignedIn?: (path: string) => void;
}

/**
 * Sign this WebView in with a session the app mints. Refuses outright without
 * the embed marker: on a desktop browser there is no app to ask, and this route
 * must never become a way to plant a session from outside.
 */
export function NativeSignIn({
  orgId,
  reason,
  returnTo,
  marker = getEmbedMarker(),
  client,
  request = requestNativeSession,
  onSignedIn = (path) => window.location.replace(path),
}: NativeSignInProps) {
  const stytch = useStytchB2BClient();
  const sessionClient = client ?? (stytch.session as unknown as HydratableSessionClient);
  const [state, setState] = useState<SignInState>({ status: 'working' });
  const [attempt, setAttempt] = useState(0);
  const started = useRef(-1);

  useEffect(() => {
    if (!marker || started.current === attempt) return;
    started.current = attempt;
    setState({ status: 'working' });
    void (async () => {
      try {
        const tokens = await request(orgId, reason);
        await hydrateNativeSession(sessionClient, tokens, consoleConfig.sessionDurationMinutes);
        onSignedIn(returnTo);
      } catch (cause) {
        const message = cause instanceof NativeSessionError
          ? nativeSessionErrorMessage(cause.code)
          : 'The app could not sign this page in.';
        console.warn('[embedded] Native sign-in failed', cause instanceof NativeSessionError ? cause.code : cause);
        setState({ status: 'failed', message });
      }
    })();
  }, [attempt, marker, onSignedIn, orgId, reason, request, returnTo, sessionClient]);

  if (!marker) {
    return (
      <main className={CALLBACK_SCREEN}>
        <section className={`${CALLBACK_CARD} native-sign-in-refused`} role="alert" data-testid="native-sign-in-refused">
          This sign-in page only works inside the Nimbalyst app.
        </section>
      </main>
    );
  }
  return (
    <main className={CALLBACK_SCREEN}>
      <section className={`${CALLBACK_CARD} native-sign-in grid justify-items-center gap-4`} data-native-sign-in={state.status}>
        {state.status === 'working' ? (
          <span role="status">Signing in…</span>
        ) : (
          <>
            <span role="alert">{state.message}</span>
            <button className={BUTTON_PRIMARY_LG} type="button" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          </>
        )}
      </section>
    </main>
  );
}

/** `/authenticate/native?orgId=<org>&returnTo=<path>`: the app's entry point. */
export function AuthenticateNativeRoute() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const orgParam = query.get('orgId');
  const orgId = orgParam && ORG_ID_PATTERN.test(orgParam) ? orgParam : null;
  const returnTo = nativeReturnPath(query.get('returnTo'));
  return <NativeSignIn orgId={orgId} reason={orgId ? 'switch-org' : 'expired'} returnTo={returnTo} />;
}

/** Shown instead of the login page when the embedded console has no session. */
export function NativeSessionGate() {
  const location = useLocation();
  const path = `${location.pathname}${location.search}${location.hash}`;
  return <NativeSignIn orgId={orgIdFromConsolePath(location.pathname)} reason="expired" returnTo={nativeReturnPath(path)} />;
}

/**
 * `/app/...` names something only the author's desktop holds. In the app it is
 * handed to native (an in-app link is a pushState, which the native navigation
 * policy never sees) and the reader stays on the page they came from.
 */
export function EmbeddedPersonalLink() {
  const location = useLocation();
  const navigate = useNavigate();
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    openPersonalInNative(`${location.pathname}${location.search}`);
    if (window.history.length > 1) navigate(-1);
  }, [location.pathname, location.search, navigate]);
  return null;
}

/** Posts `route` on every navigation and title change. Renders nothing; inert outside the app. */
export function EmbeddedRouteReporter() {
  const location = useLocation();
  const path = `${location.pathname}${location.search}${location.hash}`;
  const report = useCallback(() => postRoute(path), [path]);
  useEffect(() => {
    if (!getEmbedMarker()) return;
    report();
    // Routes set `document.title` in their own effects, after this one may run.
    const titleElement = document.querySelector('title');
    if (!titleElement) return;
    const observer = new MutationObserver(report);
    observer.observe(titleElement, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, [report]);
  return null;
}
